import React, { useEffect, useState } from "react";
import axios from "../../api/axiosInstance";
import { Link, useNavigate } from "react-router-dom";
import SERVER_URL from "../../config/server";
import Cookies from "js-cookie";
import googleLogo from "../../assets/Google__G__logo.svg.png";
import ModalComponent from "../ModalComponent";
import useAuthStore from "../../store/useAuthStore";


import { toast } from 'react-toastify';

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setPasswordVisibility] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [modalShow, setModalShow] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(60);
  const [forgotPassword, setForgotPassword] = useState(false);
  const { checkAuth } = useAuthStore();

  useEffect(() => {
    checkAuth();
    if (Cookies.get("token")) {
      navigate("/");
    }
  }, [checkAuth, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(
        "/user/login",
        { email, password },
        { withCredentials: true }
      );

      if (response.status === 200) {
        const { token } = response.data;
        Cookies.set("token", token, { expires: 7 });
        checkAuth(); // Update the auth state
        setEmail("");
        setPassword("");
        toast.success('Logged in successfully!');
        navigate("/");
      } else if (response.status === 404) {
        toast.error('Invalid credentials. Please try again.');
        setError("Incorrect Password");
      } else {
        
        setError("An unexpected error occurred. Please try again.");
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setError("Invalid credentials. Please check your email and password.");
      } else {
        setError("An error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

const handleGoogleSignIn = () => {
  const popup = window.open(
    `${SERVER_URL}/auth/google`,
    '_blank',
    'width=500,height=600'
  );

  const listener = (event) => {
    if (event.origin !== SERVER_URL) return;

    const { token } = event.data;
    if (token) {
      popup.close();
      Cookies.set("token", token, { expires: 7 });
      checkAuth();
      navigate("/");
      window.removeEventListener('message', listener); // clean up
    }
  };

  window.addEventListener('message', listener);
};


  const handleForgotPassword = () => {
    if (!email) {
      setError("Please enter your email to reset password.");
      return;
    }

    setModalShow(true);
    setForgotPassword(true);

    axios
      .post("/otp/forgot-password/send-otp", { email })
      .then(() => {
        setOtpSent(true);
        setTimer(60);
        setError("");
      })
      .catch(() => {
        setError("Failed to send reset link.");
      });
  };

  return (
    <div className="min-h-screen flex justify-center items-center mt-20 text-white px-4">
      <div className="bg-gray-900 p-8 rounded-xl shadow-lg max-w-md w-full text-center">
        <h2 className="text-3xl font-extrabold text-blue-500 mb-2">Welcome Back</h2>
        <h3 className="text-2xl font-bold text-white mb-6">Login</h3>

        <form onSubmit={handleSubmit} className="flex flex-col">
          <div className="mb-4">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full p-3 bg-gray-800 border border-gray-600 rounded-md focus:outline-none focus:border-blue-500 text-white placeholder-gray-400"
            />
          </div>

          <div className="mb-4 relative">
            <input
              type={isPasswordVisible ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full p-3 bg-gray-800 border border-gray-600 rounded-md focus:outline-none focus:border-blue-500 text-white placeholder-gray-400"
            />
            <span
              className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-300"
              onClick={() => setPasswordVisibility(!isPasswordVisible)}
            >
              {isPasswordVisible ? "🙈" : "👁️"}
            </span>
          </div>

          <div
            className="text-right text-blue-500 cursor-pointer mb-4 hover:underline"
            onClick={handleForgotPassword}
          >
            Forgot Password?
          </div>

          <ModalComponent
            show={modalShow}
            email={email}
            onHide={() => setModalShow(false)}
            timer={timer}
            otpsent={otpSent}
            forgotPassword={forgotPassword}
            onStatusChange={(status) => {
              if (status && forgotPassword) {
                toast.success("Password reset successful! Please login with your new password.");
              }
            }}
          />

          {error && <p className="text-red-500 mb-4">{error}</p>}

          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-3 rounded-md font-bold hover:bg-blue-700 transition mb-4 flex items-center justify-center"
            disabled={isLoading}
          >
            {isLoading && (
              <svg
                className="animate-spin h-5 w-5 mr-3 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
            )}
            {isLoading ? "Logging In..." : "Log In"}
          </button>
        </form>

        <div className="flex items-center justify-center mb-4">
          <div className="w-1/4 h-px bg-gray-500"></div>
          <span className="mx-2 text-gray-400">OR</span>
          <div className="w-1/4 h-px bg-gray-500"></div>
        </div>

        <button
          onClick={handleGoogleSignIn}
          className="flex items-center justify-center w-full border border-gray-600 py-3 rounded-md hover:shadow-md transition mb-4 bg-gray-800 hover:bg-gray-700"
        >
          <img src={googleLogo} alt="Google logo" className="w-5 mr-3" />
          Sign in with Google
        </button>

        <p className="text-gray-400 text-sm">
          Don't have an account?
          <Link to="/signup" className="text-blue-500 font-bold ml-1 hover:underline">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
